// Dynamic Minicart Language Fix
class MinicartLanguageFix {
  constructor() {
    this.cartDrawerSelector = '.minicart';
    this.currentLanguage = this.getCurrentLanguage();
    this.specificTranslations = {};
    this.productTranslations = {};
    this.init();
  }

  init() {
    this.loadTranslations().then(() => {
      this.loadProductTranslations();
      this.interceptCartRequests();
      this.bindLanguageChanges();
      this.observeAllMinicarts();
      this.fixCartUrls(); // Fix cart URLs on initialization
    });
  }

  getCurrentLanguage() {
    const path = window.location.pathname;
    if (path.includes('/en/') || path === '/en') return 'en';
    if (path.includes('/de/') || path === '/de') return 'de';
    if (path.includes('/fr/') || path === '/fr') return 'fr';
    if (path.includes('/es/') || path === '/es') return 'es';
    if (path.includes('/it/') || path === '/it') return 'it';
    return 'nl'; // default
  }

  getLanguageSpecificURL() {
    const baseUrl = window.location.origin;
    
    switch(this.currentLanguage) {
      case 'en': return `${baseUrl}/en`;
      case 'de': return `${baseUrl}/de`;
      case 'fr': return `${baseUrl}/fr`;
      case 'es': return `${baseUrl}/es`;
      case 'it': return `${baseUrl}/it`;
      default: return `${baseUrl}`; // nl/default
    }
  }

  getLanguagePrefix() {
    // Get language prefix for URLs
    switch(this.currentLanguage) {
      case 'en': return '/en';
      case 'de': return '/de';
      case 'fr': return '/fr';
      case 'es': return '/es';
      case 'it': return '/it';
      default: return ''; // nl/default - no prefix
    }
  }

  async loadTranslations() {
    try {
      console.log('Loading translations from current page...');
      
      // Use language-specific URL for AJAX request
      const languageURL = this.getLanguageSpecificURL();
      console.log('Fetching translations from:', languageURL);
      
      // Make AJAX request to language-specific page
      const response = await fetch(languageURL, {
        method: 'GET',
        headers: {
          'X-Requested-With': 'XMLHttpRequest',
          'Accept': 'text/html'
        }
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const html = await response.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      
      // Extract cart drawer from the response
      const cartDrawer = doc.querySelector(this.cartDrawerSelector);
      
      if (!cartDrawer) {
        console.warn('Cart drawer not found in AJAX response');
        return;
      }
      
      // Extract all text mappings from the cart drawer
      this.extractSpecificElements(cartDrawer);
      console.log('Translations loaded successfully for language:', this.currentLanguage);
      
    } catch (error) {
      console.error('Error loading translations:', error);
    }
  }

  async loadProductTranslations() {
    try {
      console.log('Loading product translations...');
      
      // Use language-specific URL for AJAX request to get product translations
      const languageURL = this.getLanguageSpecificURL();
      const response = await fetch(languageURL, {
        method: 'GET',
        headers: {
          'X-Requested-With': 'XMLHttpRequest',
          'Accept': 'text/html'
        }
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const html = await response.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      
      // Extract product translations from the current page
      const productLinks = doc.querySelectorAll('a[href*="/products/"]');
      this.productTranslations = {};
      
      productLinks.forEach(link => {
        const href = link.getAttribute('href');
        const productTitle = link.textContent.trim();
        
        if (href && productTitle) {
          // Extract product handle from URL
          const match = href.match(/\/products\/([^?]+)/);
          if (match) {
            const productHandle = match[1];
            this.productTranslations[productHandle] = productTitle;
          }
        }
      });
      
      console.log('Product translations loaded:', Object.keys(this.productTranslations).length, 'products');
      
    } catch (error) {
      console.error('Error loading product translations:', error);
    }
  }

  extractSpecificElements(cartDrawer) {
    // Clear previous specific element translations
    this.specificTranslations = {};
    
    // Button texts
    const viewCartBtn = cartDrawer.querySelector('.btn-cart');
    if (viewCartBtn) {
      this.specificTranslations['.btn-cart'] = viewCartBtn.textContent.trim();
    }
    
    const checkoutBtn = cartDrawer.querySelector('.btn-checkout');
    if (checkoutBtn) {
      this.specificTranslations['.btn-checkout'] = checkoutBtn.textContent.trim();
    }
    
    const backToShopBtn = cartDrawer.querySelector('.cart__return .btn');
    if (backToShopBtn) {
      this.specificTranslations['.cart__return .btn'] = backToShopBtn.textContent.trim();
    }
    
    // Subtotals
    const subtotalEl = cartDrawer.querySelector('.cart__total strong');
    if (subtotalEl) {
      this.specificTranslations['.cart__total strong'] = subtotalEl.textContent.trim();
    }
    
    // Empty cart message
    const emptyCartEl = cartDrawer.querySelector('.cart__empty');
    if (emptyCartEl) {
      this.specificTranslations['.cart__empty'] = emptyCartEl.textContent.trim();
    }
    
    // Free shipping messages
    const freeShippingDesc = cartDrawer.querySelector('.cart__free-shipping_desc');
    if (freeShippingDesc) {
      this.specificTranslations['.cart__free-shipping_desc'] = freeShippingDesc.innerHTML;
    }
    
    // Continue shopping text
    const continueShopping = cartDrawer.querySelector('.cart__free-shipping_back');
    if (continueShopping) {
      this.specificTranslations['.cart__free-shipping_back'] = continueShopping.innerHTML;
    }
    
    // Terms and conditions
    const termsLabel = cartDrawer.querySelector('.cart__condition label');
    if (termsLabel) {
      this.specificTranslations['.cart__condition label'] = termsLabel.innerHTML;
    }
    
    // Remove button title
    const removeBtn = cartDrawer.querySelector('.item__remove');
    if (removeBtn) {
      this.specificTranslations['.item__remove'] = removeBtn.getAttribute('title');
    }
  }

  updateCartDrawerTexts() {
    const allMinicarts = this.findAllMinicarts();
    if (!allMinicarts || allMinicarts.length === 0) {
      console.log('No cart drawers found');
      return;
    }

    let totalUpdatedCount = 0;

    // Update each minicart found on the page
    allMinicarts.forEach((currentCartDrawer, index) => {
      let updatedCount = 0;

      // Update specific elements by selector
      for (const [selector, newContent] of Object.entries(this.specificTranslations)) {
        if (selector.startsWith('.')) {
          const elements = currentCartDrawer.querySelectorAll(selector);
          
          elements.forEach(element => {
            if (typeof newContent === 'string') {
              if (selector === '.item__remove' && newContent) {
                // Update title attribute for remove buttons
                if (element.getAttribute('title') !== newContent) {
                  element.setAttribute('title', newContent);
                  updatedCount++;
                }
              } else if (element.innerHTML !== newContent && element.textContent !== newContent) {
                // Update innerHTML for complex content, textContent for simple text
                if (newContent.includes('<')) {
                  element.innerHTML = newContent;
                } else {
                  element.textContent = newContent;
                }
                updatedCount++;
              }
            }
          });
        }
      }

      // Update product titles
      updatedCount += this.updateProductTitles(currentCartDrawer);

      console.log(`Updated ${updatedCount} text elements in cart drawer ${index + 1}`);
      totalUpdatedCount += updatedCount;
    });

    console.log(`Total: Updated ${totalUpdatedCount} text elements across ${allMinicarts.length} cart drawers`);
    
    // Fix cart URLs after updating texts
    this.fixCartUrls();
  }

  updateProductTitles(cartDrawer) {
    let productUpdates = 0;
    const productTitleLinks = cartDrawer.querySelectorAll('.item__name a');
    
    productTitleLinks.forEach(link => {
      const href = link.getAttribute('href');
      if (href) {
        // Extract product handle from URL
        const match = href.match(/\/products\/([^?]+)/);
        if (match) {
          const productHandle = match[1];
          const translatedTitle = this.productTranslations[productHandle];
          
          if (translatedTitle && link.textContent !== translatedTitle) {
            console.log('Updating product title:', link.textContent, '->', translatedTitle);
            link.textContent = translatedTitle;
            productUpdates++;
          }
        }
      }
    });
    
    return productUpdates;
  }

  // Method to fix cart URLs with language prefix
  fixCartUrls() {
    const allMinicarts = this.findAllMinicarts();
    if (!allMinicarts || allMinicarts.length === 0) return;

    const languagePrefix = this.getLanguagePrefix();
    let fixedUrlCount = 0;

    allMinicarts.forEach((minicart, index) => {
      // Fix cart page links (VEDI CARRELLO button)
      const cartLinks = minicart.querySelectorAll('a[href*="/cart"]');
      
      cartLinks.forEach(link => {
        const href = link.getAttribute('href');
        if (href && href.startsWith('/cart') && !href.startsWith(languagePrefix + '/cart')) {
          // Only fix if it's a simple /cart URL without language prefix
          const newHref = languagePrefix + href;
          if (href !== newHref) {
            link.setAttribute('href', newHref);
            console.log(`Fixed cart URL in minicart ${index + 1}: ${href} -> ${newHref}`);
            fixedUrlCount++;
          }
        }
      });

      // Fix checkout page links if they exist in minicart
      const checkoutLinks = minicart.querySelectorAll('a[href*="/checkout"]');
      
      checkoutLinks.forEach(link => {
        const href = link.getAttribute('href');
        if (href && href.startsWith('/checkout') && !href.startsWith(languagePrefix + '/checkout')) {
          // Only fix if it's a simple /checkout URL without language prefix
          const newHref = languagePrefix + href;
          if (href !== newHref) {
            link.setAttribute('href', newHref);
            console.log(`Fixed checkout URL in minicart ${index + 1}: ${href} -> ${newHref}`);
            fixedUrlCount++;
          }
        }
      });

      // NEW: Fix "Back to shop" links (Volver a la tienda)
      const backToShopLinks = minicart.querySelectorAll('.cart__return a[href*="/collections/all"]');
      
      backToShopLinks.forEach(link => {
        const href = link.getAttribute('href');
        if (href && href.startsWith('/collections/all') && !href.startsWith(languagePrefix + '/collections/all')) {
          // Only fix if it's a simple /collections/all URL without language prefix
          const newHref = languagePrefix + href;
          if (href !== newHref) {
            link.setAttribute('href', newHref);
            console.log(`Fixed back to shop URL in minicart ${index + 1}: ${href} -> ${newHref}`);
            fixedUrlCount++;
          }
        }
      });

      // NEW: Also fix any other collection links that might be used for "Back to shop"
      const collectionLinks = minicart.querySelectorAll('.cart__return a[href*="/collections/"]');
      
      collectionLinks.forEach(link => {
        const href = link.getAttribute('href');
        // Check if it's a collection link that doesn't have the language prefix
        if (href && href.startsWith('/collections/') && !href.startsWith(languagePrefix + '/collections/')) {
          const newHref = languagePrefix + href;
          if (href !== newHref) {
            link.setAttribute('href', newHref);
            console.log(`Fixed collection URL in minicart ${index + 1}: ${href} -> ${newHref}`);
            fixedUrlCount++;
          }
        }
      });
    });

    if (fixedUrlCount > 0) {
      console.log(`Fixed ${fixedUrlCount} URLs across ${allMinicarts.length} minicarts`);
    }
  }

  interceptCartRequests() {
    const self = this;
    
    // Intercept all cart AJAX requests
    const originalFetch = window.fetch;
    window.fetch = function(...args) {
      const url = args[0];
      
      if (typeof url === 'string' && (
          url.includes('/cart/add.js') ||
          url.includes('/cart/change.js') ||
          url.includes('/cart/update.js') ||
          url.includes('/cart/clear.js')
      )) {
        return originalFetch.apply(this, args).then(async response => {
          // Reload translations and update texts after cart update
          await self.loadTranslations();
          await self.loadProductTranslations();
          setTimeout(() => {
            self.updateCartDrawerTexts();
          }, 500);
          return response;
        });
      }
      
      return originalFetch.apply(this, args);
    };

    // Also intercept XMLHttpRequest for older AJAX calls
    const originalXHROpen = XMLHttpRequest.prototype.open;
    XMLHttpRequest.prototype.open = function(method, url, async, user, password) {
      this.addEventListener('load', function() {
        if (url.includes('/cart/add.js') || url.includes('/cart/change.js')) {
          setTimeout(async () => {
            await self.loadTranslations();
            await self.loadProductTranslations();
            self.updateCartDrawerTexts();
          }, 500);
        }
      });
      return originalXHROpen.call(this, method, url, async, user, password);
    };
  }

  bindLanguageChanges() {
    // Update when language changes
    document.addEventListener('click', (e) => {
      const target = e.target.closest('a');
      if (target && target.href && this.isLanguageLink(target.href)) {
        // Update current language and reload translations after navigation
        setTimeout(() => {
          this.currentLanguage = this.getCurrentLanguage();
          this.loadTranslations().then(() => {
            this.loadProductTranslations();
            this.updateCartDrawerTexts();
          });
        }, 1000);
      }
    });

    document.addEventListener('change', (e) => {
      if (e.target.matches('select[name="locale"], select[data-language-selector]')) {
        // Update current language and reload translations after navigation
        setTimeout(() => {
          this.currentLanguage = this.getCurrentLanguage();
          this.loadTranslations().then(() => {
            this.loadProductTranslations();
            this.updateCartDrawerTexts();
          });
        }, 1000);
      }
    });
  }

  isLanguageLink(href) {
    return href.includes('/en') || href.includes('/nl') || 
           href.includes('/fr') || href.includes('/de') ||
           href.includes('/es') || href.includes('/it');
  }

  findMinicart() {
    // Keep this for backward compatibility, returns first minicart
    return document.querySelector(this.cartDrawerSelector);
  }

  findAllMinicarts() {
    // Get all minicart elements
    return Array.from(document.querySelectorAll(this.cartDrawerSelector));
  }

  observeAllMinicarts() {
    // Observe all minicart elements instead of just one
    const allMinicarts = this.findAllMinicarts();
    
    allMinicarts.forEach((minicart, index) => {
      const observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
            if (minicart.classList.contains('opened')) {
              // Update texts when minicart opens
              setTimeout(() => {
                this.updateCartDrawerTexts();
              }, 100);
            }
          }
          
          // Watch for content changes in the minicart (new items added)
          if (mutation.type === 'childList') {
            let shouldUpdate = false;
            mutation.addedNodes.forEach(node => {
              if (node.nodeType === 1) {
                if (node.matches && (node.matches('.cart__item') || node.querySelector('.cart__item'))) {
                  shouldUpdate = true;
                }
              }
            });
            
            if (shouldUpdate) {
              setTimeout(() => {
                this.updateCartDrawerTexts();
              }, 100);
            }
          }
        });
      });

      observer.observe(minicart, {
        attributes: true,
        attributeFilter: ['class'],
        childList: true,
        subtree: true
      });
      
      console.log(`Observing minicart ${index + 1}`);
    });
    
    console.log(`Observing ${allMinicarts.length} minicarts`);
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    window.minicartFix = new MinicartLanguageFix();
  });
} else {
  window.minicartFix = new MinicartLanguageFix();
}

// Manual update function
window.updateCartTexts = function() {
  if (window.minicartFix) {
    window.minicartFix.updateCartDrawerTexts();
  }
};

// Also update when page becomes visible (after tab switch)
document.addEventListener('visibilitychange', function() {
  if (!document.hidden && window.minicartFix) {
    setTimeout(() => {
      window.minicartFix.updateCartDrawerTexts();
    }, 500);
  }
});